import { ReactElement, useEffect, useState } from "react";
import { IGaugeConfig, ISegment } from "../../../../../components/charts/types";
import { getPalette, getPaletteColor } from "../../../../../configuration/configuration";
import { IParentSize, IRealtimeData } from "../../../../../types";
import { IDashboardComponent, IGaugeIndicatorConfig } from "../../../types";
import GaugeChartV2 from "../../../../../components/charts/gaugeChartV2";
import { getConditionResult, getStatusClass } from "../../../utils";
import { getFloatValue } from "../../../../../system/utils";

interface IProps {
  parentSize: IParentSize;
  realtime: Record<string, IRealtimeData>;
  component: IDashboardComponent<IGaugeIndicatorConfig>;
}

const GaugeWrapper = ({ parentSize, realtime, component }: IProps): ReactElement| null => {
  const [ gaugeConfig, setGaugeConfig ] = useState<Record<string, IGaugeConfig>>({});

  useEffect(() => {
    const config: Record<string, IGaugeConfig> = {};

    component.config.variables.map(variable => {
      const segments: ISegment[] = [];
      if(variable.segments.length === 0) {
        segments.push(
          { start: variable.minimum ?? 0, end: variable.maximum ?? 100, color: getPalette().statusColor.empty.background }
        );
      } else {
        variable.segments.forEach(s => {
          const minimum = s.start.type === "VALUE" ? s.start.value as number : getFloatValue(realtime[s.start.value], 0);
          const maximum = s.end.type === "VALUE" ? s.end.value as number : getFloatValue(realtime[s.end.value], 100);

          segments.push({ start: minimum, end: maximum, color: s.color });
        });
      }

      config[variable.hash] = {
        width: parentSize.width * (component.config.scale ?? 1),
        height: parentSize.height  * (component.config.scale ?? 1) - 20,
        minimum: variable.minimum ?? 0,
        maximum: variable.maximum ?? 100,
        start: 270,
        length: 180,
        color: variable.color ?? undefined,
        segments,
        format: n => n.toFixed(variable.decimals)
      };
    });

    setGaugeConfig(config);
  }, [ component, realtime, parentSize ]);

  const variable0 = component.config.variables[0];
  const value = getFloatValue(realtime[variable0.code], 0);
  const hasUnit = variable0.unit && variable0.unit !== "";
  const condition = getConditionResult(value, variable0.conditions ?? [], realtime);
  const errorClass = getStatusClass(condition ? condition.severity : null);
  const valueColor = errorClass === "" ? variable0.color ?? getPaletteColor() : "whitesmoke";

  return gaugeConfig[variable0.hash]
    ? <div className={`display-grid align-items-stretch h-100 ${errorClass}`} style={{ gridTemplateRows: "auto auto 1fr" }}>
      <strong style={{ textAlign: "center", color: component.foreground }}>
        {variable0.title}
        {hasUnit && <small style={{ fontSize: "0.5em" }}>{` [${variable0.unit}]`}</small>}
      </strong>
      <strong style={{ color: valueColor, fontSize: "2em", textAlign: "center" }} >{value?.toFixed(variable0.decimals)}</strong>
      <div style={{ overflow: "hidden" }}>
        <GaugeChartV2 gaugeConfig={gaugeConfig[variable0.hash]} value={value}  />
      </div>
    </div>
    : null;
};

export default GaugeWrapper;
